import { useState, useEffect, useCallback, useRef } from 'react';
import {
  UseServerDataManagerParams,
  UseServerDataManagerReturn,
  ServerQueryParams,
  ServerPaginationParams,
  ServerSortingParams,
  Filter,
} from '../../types';
import { useDebounce } from '../utils/use-debounce';

/**
 * Hook for managing server-side data fetching with pagination, sorting, filtering, and search
 *
 * This hook provides comprehensive server-side data management for tables,
 * including automatic debouncing, loading states, error handling, and query parameter management.
 *
 * @template T - The type of data rows returned from the server
 * @param params - Configuration object containing:
 *   - fetcher: Function to fetch data from server
 *   - initialParams: Initial query parameters
 *   - debounceMs: Debounce delay for search queries (default: 300ms)
 *   - onError: Optional error handler
 *
 * @returns Object containing:
 *   - data: Current data array
 *   - total: Total number of records on server
 *   - isLoading: Loading state
 *   - error: Current error state
 *   - queryParams: Current query parameters
 *   - setSearch: Function to update search term
 *   - setFilters: Function to update filters
 *   - setSorting: Function to update sorting
 *   - setPagination: Function to update pagination
 *   - refetch: Function to manually refetch data
 *
 * @example
 * ```tsx
 * const {
 *   data,
 *   total,
 *   isLoading,
 *   setSearch,
 *   setPagination
 * } = useServerDataManager({
 *   fetcher: async (params) => {
 *     const response = await api.getData(params);
 *     return { data: response.items, total: response.total };
 *   },
 *   initialParams: { pagination: { page: 0, pageSize: 20 } },
 *   onError: (error) => console.error('Data fetch failed:', error)
 * });
 * ```
 */
export function useServerDataManager<T extends Record<string, unknown>>({
  fetcher,
  initialParams = {},
  debounceMs = 300,
  onError,
}: UseServerDataManagerParams<T>): UseServerDataManagerReturn<T> {
  // State management
  const [data, setData] = useState<T[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | undefined>();

  // Query parameters state
  const [queryParams, setQueryParams] = useState<ServerQueryParams>({
    search: initialParams.search || '',
    filters: initialParams.filters || [],
    sorting: initialParams.sorting,
    pagination: initialParams.pagination || { page: 0, pageSize: 10 },
  });

  // Debounce search to reduce API calls
  const debouncedSearch = useDebounce(queryParams.search || '', debounceMs);

  // Track the current request to cancel outdated ones
  const currentRequestRef = useRef<AbortController | null>(null);

  // Fetch data function
  const fetchData = useCallback(
    async (params: ServerQueryParams) => {
      try {
        // Cancel previous request
        if (currentRequestRef.current) {
          currentRequestRef.current.abort();
        }

        // Create new abort controller
        const abortController = new AbortController();
        currentRequestRef.current = abortController;

        setIsLoading(true);
        setError(undefined);

        // Call the fetcher with current params
        const response = await fetcher(params);

        // Check if request was cancelled
        if (abortController.signal.aborted) {
          return;
        }

        setData(response.data);
        setTotal(response.total);
      } catch (err) {
        // Don't set error if request was cancelled
        if (err instanceof Error && err.name === 'AbortError') {
          return;
        }

        const error =
          err instanceof Error ? err : new Error('Unknown error occurred');
        setError(error);
        onError?.(error);
      } finally {
        setIsLoading(false);
        currentRequestRef.current = null;
      }
    },
    [fetcher, onError],
  );

  // Refetch function for manual refresh
  const refetch = useCallback(async () => {
    await fetchData({ ...queryParams, search: debouncedSearch });
  }, [fetchData, queryParams, debouncedSearch]);

  // Effect to fetch data when parameters change
  useEffect(() => {
    const params = { ...queryParams, search: debouncedSearch };
    fetchData(params);
  }, [debouncedSearch, queryParams, fetchData]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (currentRequestRef.current) {
        currentRequestRef.current.abort();
      }
    };
  }, []);

  // Update functions
  const setSearch = useCallback((search: string) => {
    setQueryParams((prev) => ({
      ...prev,
      search,
      // Reset to first page when searching
      pagination: prev.pagination ? { ...prev.pagination, page: 0 } : undefined,
    }));
  }, []);

  const setFilters = useCallback((filters: Filter[]) => {
    setQueryParams((prev) => ({
      ...prev,
      filters,
      // Reset to first page when filtering
      pagination: prev.pagination ? { ...prev.pagination, page: 0 } : undefined,
    }));
  }, []);

  const setSorting = useCallback((sorting: ServerSortingParams | undefined) => {
    setQueryParams((prev) => ({
      ...prev,
      sorting,
      // Reset to first page when sorting
      pagination: prev.pagination ? { ...prev.pagination, page: 0 } : undefined,
    }));
  }, []);

  const setPagination = useCallback((pagination: ServerPaginationParams) => {
    setQueryParams((prev) => ({
      ...prev,
      pagination,
    }));
  }, []);

  return {
    data,
    total,
    isLoading,
    error,
    queryParams: { ...queryParams, search: debouncedSearch },
    setSearch,
    setFilters,
    setSorting,
    setPagination,
    refetch,
  };
}

/**
 * Utility function to create a mock server data fetcher for testing
 */
export function createMockServerDataFetcher<T extends Record<string, unknown>>(
  mockData: T[],
  delay = 500,
) {
  return async (params: ServerQueryParams) => {
    // Simulate network delay
    await new Promise((resolve) => setTimeout(resolve, delay));

    let filteredData = [...mockData];

    // Apply search
    if (params.search?.trim()) {
      const searchTerm = params.search.toLowerCase();
      filteredData = filteredData.filter((item) =>
        Object.values(item).some((value) =>
          String(value).toLowerCase().includes(searchTerm),
        ),
      );
    }

    // Apply filters
    if (params.filters?.length) {
      filteredData = filteredData.filter((item) => {
        return params.filters!.every((filter) => {
          const fieldValue = item[filter.field];

          switch (filter.operator) {
            case 'equals':
              return fieldValue === filter.value;
            case 'contains':
              return String(fieldValue)
                .toLowerCase()
                .includes(String(filter.value).toLowerCase());
            case 'startsWith':
              return String(fieldValue)
                .toLowerCase()
                .startsWith(String(filter.value).toLowerCase());
            case 'endsWith':
              return String(fieldValue)
                .toLowerCase()
                .endsWith(String(filter.value).toLowerCase());
            case 'greaterThan':
              return Number(fieldValue) > Number(filter.value);
            case 'lessThan':
              return Number(fieldValue) < Number(filter.value);
            case 'greaterThanOrEqual':
              return Number(fieldValue) >= Number(filter.value);
            case 'lessThanOrEqual':
              return Number(fieldValue) <= Number(filter.value);
            case 'in':
              return (
                Array.isArray(filter.value) && filter.value.includes(fieldValue)
              );
            case 'notIn':
              return (
                !Array.isArray(filter.value) ||
                !filter.value.includes(fieldValue)
              );
            default:
              return true;
          }
        });
      });
    }

    // Apply sorting
    if (params.sorting) {
      filteredData.sort((a, b) => {
        const aValue = a[params.sorting!.field];
        const bValue = b[params.sorting!.field];

        let comparison = 0;
        if ((aValue as any) < (bValue as any)) comparison = -1;
        if ((aValue as any) > (bValue as any)) comparison = 1;

        return params.sorting!.direction === 'desc' ? -comparison : comparison;
      });
    }

    const total = filteredData.length;

    // Apply pagination
    let paginatedData = filteredData;
    if (params.pagination) {
      const { page, pageSize } = params.pagination;
      const startIndex = page * pageSize;
      paginatedData = filteredData.slice(startIndex, startIndex + pageSize);
    }

    return {
      data: paginatedData,
      total,
      page: params.pagination?.page || 0,
      pageSize: params.pagination?.pageSize || total,
      totalPages: params.pagination
        ? Math.ceil(total / params.pagination.pageSize)
        : 1,
    };
  };
}
